import {
  levelForPoints,
  type CurrentUser,
  type LangueParlee,
  type NotificationSettings,
  type PrivacySettings,
  type PublicUser,
} from '@sirocco/shared';
import { paths } from './paths';
import { readMany, readValue } from './rtdb';
import { t, langue, type AutreLangue } from '../i18n';
import { versListe, type CarteDeLangues } from '../utils/langues';

/**
 * Formes stockees dans la base, et leur traduction vers les types de
 * l'application.
 *
 * La base range ses ensembles en `{ cle: true }` et ses dates en
 * millisecondes ; l'application, elle, manipule des tableaux et des
 * chaines ISO. La conversion se fait ici, une fois pour toutes, et
 * nulle part ailleurs.
 */

/** Un profil, tel qu'il est ecrit dans `users/{uid}`. */
export interface UserRecord {
  username: string;
  firstName: string;
  lastName: string;
  avatarUrl: string | null;
  level: number;
  bio: string;
  /** Le compte de l'application : voir `PublicUser.officiel`. */
  officiel?: boolean;
  /** Un compte verifie, la coche et rien de plus : voir `PublicUser.verifie`. */
  verifie?: boolean;
  /** L'emoji a cote du nom : voir `PublicUser.badge`. */
  badge?: string;
  email: string;
  birthDate: string;
  /** Absent tant que le membre n'a pas touche au reglage. */
  nightMode?: boolean;
  currentServerId: string | null;
  totalPoints: number;
  createdAt: number;
  /** Dernier passage, en millisecondes. Sert a calculer "en ligne". */
  lastSeenAt?: number;
  /** Les sessions ouvertes, effacees par le serveur a la deconnexion. */
  connections?: Record<string, true>;
  /** Ce membre parle le langage des connexions : voir signalerPresence. */
  presenceV2?: boolean;
  privacy: PrivacySettings;
  /** Absent tant que le membre n'a touche a aucune notification. */
  notifications?: NotificationSettings;
  /** Les langues qu'il parle. Absent sur les comptes d'avant ce champ. */
  languesParlees?: CarteDeLangues;
  destinations?: Record<
    string,
    {
      serverName: string;
      serverNames?: Partial<Record<AutreLangue, string>>;
      points: number;
      trophiesUnlocked: number;
      trophiesTotal: number;
      accent: string;
      sansTrophees?: boolean;
    }
  >;
}

/**
 * Le niveau d'un membre, deduit de ses points.
 *
 * On additionne les destinations plutot que de lire `record.level` : ce
 * champ existe toujours, mais comme miroir pour les lecteurs exterieurs —
 * scripts, migrations, exports. L'application ne s'y fie pas. Deux
 * chiffres tenus d'accord a la main finissent par se contredire, et ce
 * sont les points par serveur qui font foi.
 */
function levelOf(record: UserRecord): number {
  const total = Object.values(record.destinations ?? {}).reduce(
    (sum, destination) => sum + (destination.points ?? 0),
    0,
  );
  return levelForPoints(total);
}

/**
 * Au-dela de ce delai sans signe de vie, un membre n'est plus "en ligne".
 *
 * Le battement de coeur ecrit toutes les deux minutes (`HEARTBEAT_MS`) :
 * trois minutes tolerent un battement en retard, sans laisser trainer un
 * « En ligne » des minutes apres le depart. Exporte : la surveillance
 * vivante de `watchPresence` doit juger avec la meme fenetre que les
 * profils charges d'un bloc.
 */
export const ONLINE_WINDOW_MS = 3 * 60 * 1000;

/** Reglages par defaut d'un nouveau compte : profil ouvert. */
export const DEFAULT_PRIVACY: PrivacySettings = {
  profileVisibility: 'server',
  showEvents: true,
  showTrophies: true,
  showPosts: true,
  showOnlineStatus: true,
  shareLocation: false,
  destinationsFriendsOnly: false,
};

/**
 * Tout est accepte au depart.
 *
 * Une notification qu'on n'a pas demandee se coupe apres l'avoir vue ;
 * une notification qu'on n'a jamais recue ne se demande pas, parce qu'on
 * ignore qu'elle existe.
 */
export const DEFAULT_NOTIFICATIONS: NotificationSettings = {
  messages: true,
  calls: true,
  friends: true,
  friendPosts: true,
  events: true,
  eventRequests: true,
  eventAccepted: true,
  eventLeaves: true,
  activity: true,
  mentions: true,
  trophies: true,
};

/**
 * Ce qu'un autre membre a le droit de voir.
 * Le statut en ligne depend a la fois de l'activite recente et du
 * reglage de confidentialite : couper le partage doit eteindre la
 * pastille verte immediatement.
 */
export function toPublicUser(uid: string, record: UserRecord): PublicUser {
  const privacy = record.privacy ?? DEFAULT_PRIVACY;
  /*
    Deux langages, comme dans watchPresence : les connexions quand le
    membre les parle, l'age du battement sinon. Voir signalerPresence.
  */
  const seenRecently = record.presenceV2
    ? Object.keys(record.connections ?? {}).length > 0
    : Boolean(record.lastSeenAt && Date.now() - record.lastSeenAt < ONLINE_WINDOW_MS);
  return {
    id: uid,
    username: record.username,
    ...(record.officiel ? { officiel: true as const } : {}),
    ...(record.verifie ? { verifie: true as const } : {}),
    ...(record.badge ? { badge: record.badge } : {}),
    firstName: record.firstName,
    lastName: record.lastName,
    avatarUrl: record.avatarUrl ?? null,
    level: levelOf(record),
    bio: record.bio ?? '',
    online: privacy.showOnlineStatus && seenRecently,
  };
}

/** Le profil complet, reserve au membre connecte. */
/**
 * Les langues d'un membre.
 *
 * Un compte d'avant ce champ n'a rien declare : on lui prete celle dans
 * laquelle il lit l'application, la seule qu'on soit sur qu'il comprenne.
 */
function languesDe(record: UserRecord): LangueParlee[] {
  const declarees = versListe(record.languesParlees);
  return declarees.length ? declarees : [langue];
}

export function toCurrentUser(uid: string, record: UserRecord): CurrentUser {
  return {
    ...toPublicUser(uid, record),
    email: record.email,
    birthDate: record.birthDate,
    nightMode: record.nightMode ?? false,
    currentServerId: record.currentServerId ?? null,
    /*
      Le total se deduit des villes, il ne se lit pas.

      `record.totalPoints` etait une troisieme copie du meme chiffre,
      ecrite par le client seul : elle a decroche des que le moteur s'est
      mis a donner des trophees, et le profil affichait moins que le
      classement. Le detail par ville fait foi — c'est deja ce que dit
      `levelOf` juste au-dessus.
    */
    totalPoints: Object.values(record.destinations ?? {}).reduce(
      (somme, destination) => somme + (destination.points ?? 0),
      0,
    ),
    privacy: record.privacy ?? DEFAULT_PRIVACY,
    notifications: record.notifications ?? DEFAULT_NOTIFICATIONS,
    languesParlees: languesDe(record),
    createdAt: new Date(record.createdAt ?? Date.now()).toISOString(),
    /*
      Le spot courant d'abord, puis les autres du plus fourni au moins
      fourni. Sans ce tri, Firebase les rendrait dans l'ordre de leurs
      cles — c'est-a-dire alphabetique, ce qui n'a aucun sens pour le
      membre qui les lit.
    */
    destinations: Object.entries(record.destinations ?? {})
      .map(([serverId, destination]) => ({
        serverId,
        serverName:
          (langue === 'fr' ? undefined : destination.serverNames?.[langue]) || destination.serverName,
        points: destination.points ?? 0,
        trophiesUnlocked: destination.trophiesUnlocked ?? 0,
        trophiesTotal: destination.trophiesTotal ?? 0,
        accent: destination.accent,
        ...(destination.sansTrophees ? { sansTrophees: true } : {}),
      }))
      .sort((a, b) => {
        if (a.serverId === record.currentServerId) return -1;
        if (b.serverId === record.currentServerId) return 1;
        return b.points - a.points;
      }),
  };
}

/** Membre supprime ou introuvable : un profil neutre vaut mieux qu'un plantage. */
export function unknownUser(uid: string): PublicUser {
  return {
    id: uid,
    username: t.commun.membreInconnu,
    firstName: '',
    lastName: '',
    avatarUrl: null,
    level: 1,
    bio: '',
    online: false,
  };
}

export async function loadUser(uid: string): Promise<PublicUser> {
  const record = await readValue<UserRecord>(paths.user(uid));
  return record ? toPublicUser(uid, record) : unknownUser(uid);
}

/**
 * Charge plusieurs profils d'un coup.
 * Un feed affiche dix auteurs : dix lectures en parallele, pas dix
 * allers-retours en file.
 */
export async function loadUsers(uids: string[]): Promise<Map<string, PublicUser>> {
  const records = await readMany<UserRecord>(paths.user, uids);
  const users = new Map<string, PublicUser>();
  for (const uid of new Set(uids)) {
    const record = records.get(uid);
    users.set(uid, record ? toPublicUser(uid, record) : unknownUser(uid));
  }
  return users;
}

/** Millisecondes -> chaine ISO, le format manipule par l'application. */
export function toIso(millis: number | undefined): string {
  return new Date(millis ?? Date.now()).toISOString();
}

/** `{ cle: true }` -> `['cle']`, la forme de tous les index de la base. */
export function keysOf(value: Record<string, unknown> | null | undefined): string[] {
  return value ? Object.keys(value) : [];
}
